import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { axiosApiInstance } from "../../app/axios";
import { API_URL } from "../../app/config";
import FlightRoute from "./FlightRoute";
import FlightPrice from "./FlightPrice";
import FlightDetailsTabs from "./FlightDetailsTabs";
import { useAppSelector } from "../../app/hooks";
import { useLocation, useNavigate } from "react-router";
import { extractPaxFromUrl } from "../../_helpers/formatUtils";

interface FlightCardProps {
  route: any;
  routeIndex: number;
  prefix?: string;
  index?: number;
  selectedFlightPriceId?: number;
  handleSelectFlight?: (
    priceDetails: any,
    route: any,
    type: string,
    index: number
  ) => void;
}

const FlightMainCard = ({
  route,
  routeIndex,
  prefix,
  handleSelectFlight,
  selectedFlightPriceId,
  index,
}: FlightCardProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const etraveFlightData: any = useAppSelector(
    (state) => state.flight.etraveFlightData
  );

  const flightSearchDetails = useAppSelector(
    (state) => state.home.flightSearchDetails
  );

  const { ADULT, CHILD, INFANT } = flightSearchDetails;

  const sortedPriceList = useMemo(
    () =>
      [...route?.Fares].sort(
        (a, b) =>
          a?.FareDetails[0]?.Total_Amount - b?.FareDetails[0]?.Total_Amount
      ),
    [route?.Fares]
  );
  const [fareRulesLoading, setFareRulesLoading] = useState(false);
  const [openFlightRate, setOpenFlightRate] = useState<boolean>(false);
  const [openFightDetail, setOpenFlightDetail] = useState<boolean>(false);
  const [priceDetails, setPriceDetails] = useState<any>(null);
  const [fareRule, setFareRule] = useState<any>();

  const [adultBaseRate, setAdultBaseRate] = useState(0);
  const [childBaseRate, setChildBaseRate] = useState(0);
  const [infantBaseRate, setInfantBaseRate] = useState(0);

  const [adultTaxRate, setAdultTaxRate] = useState(0);
  const [childTaxRate, setChildTaxRate] = useState(0);
  const [infantTaxRate, setInfantTaxRate] = useState(0);

  const [adultFreeBaggage, setAdultFreeBaggage] = useState({});
  const [childFreeBaggage, setChildFreeBaggage] = useState({});
  const [infantFreeBaggage, setInfantFreeBaggage] = useState({});

  const getFareRules = async (Fare_Id: any, Flight_Key: string) => {
    try {
      setFareRulesLoading(true);
      const Request_Id = sessionStorage.getItem("request_id");
      const response = await axiosApiInstance.post(
        `${API_URL}/AirlineFareRule/Air_FareRule`,
        {
          Fare_Id: Fare_Id,
          Search_Key: etraveFlightData?.Search_Key || "",
          Flight_Key: Flight_Key,
          Request_Id,
        }
      );
      setFareRule(response.data?.FareRules);
    } catch (error) {
      console.error("Error fetching fare rules results:", error);
    } finally {
      setFareRulesLoading(false);
    }
  };

  useEffect(() => {
    if (sortedPriceList) {
      let minPriceOption = route.Fares[0];
      let minPrice = calculateTotalPrice(minPriceOption);

      route.Fares.forEach((fare: any) => {
        const totalPrice = calculateTotalPrice(fare);
        if (totalPrice < minPrice) {
          minPrice = totalPrice;
          minPriceOption = fare;
        }
      });

      setPriceDetails(minPriceOption);
    }
  }, [sortedPriceList]);

  const calculateTotalPrice = (fares: any) => {
    let totalAdultPrice = 0;
    let totalChildPrice = 0;
    let totalInfantPrice = 0;

    fares.FareDetails.forEach((fare: any) => {
      // const totalAmount = fare.Total_Amount || 0;
      const totalAmount =
        (fare.Total_Amount || 0) - (fare.Service_Fee_Amount || 0);

      switch (fare.PAX_Type) {
        case 0:
          totalAdultPrice += totalAmount * ADULT || 0;
          break;
        case 1:
          totalChildPrice += totalAmount * CHILD || 0;
          break;
        case 2:
          totalInfantPrice += totalAmount * INFANT || 0;
          break;
      }
    });

    return totalAdultPrice + totalChildPrice + totalInfantPrice;
  };

  const handleRadioButtonChange = (id: any) => {
    const selectedTp = sortedPriceList.find(
      (fare: any) => fare?.Fare_Id === id
    );
    setPriceDetails(selectedTp);
    if (openFightDetail) {
      getFareRules(id, route?.Flight_Key);
    }
  };

  const handleBookNowClick = (priceDetails: any, route: any) => {
    let flightDetails = JSON.parse(
      localStorage.getItem("flightDetails") || "{}"
    );

    const paxes = extractPaxFromUrl(location.search);

    flightDetails = {
      ...flightDetails,
      lastFlightSearchUrl: window.location.href,
    };

    let totalAdultPrice = 0;
    let totalChildPrice = 0;
    let totalInfantPrice = 0;

    priceDetails.FareDetails.forEach((fare: any) => {
      const totalAmount =
        (fare.Total_Amount || 0) - (fare.Service_Fee_Amount || 0);

      switch (fare.PAX_Type) {
        case 0:
          totalAdultPrice += totalAmount * ADULT || 0;
          break;
        case 1:
          totalChildPrice += totalAmount * CHILD || 0;
          break;
        case 2:
          totalInfantPrice += totalAmount * INFANT || 0;
          break;
        default:
          break;
      }
    });

    const totalPrice = totalAdultPrice + totalChildPrice + totalInfantPrice;

    localStorage.setItem("flightDetails", JSON.stringify(flightDetails));
    sessionStorage.setItem("latestfarePrice", `${totalPrice}` || "");

    const statePayload = {
      ADULT: paxes?.ADULT,
      CHILD: paxes?.CHILD,
      INFANT: paxes?.INFANT,
      tripType: paxes?.tripType,
      from: paxes?.fromLocation,
      to: paxes?.toLocation,
      Search_Key: etraveFlightData?.Search_Key,
      AirRepriceRequests: [
        {
          Flight_Key: route?.Flight_Key,
          Fare_Id: priceDetails?.Fare_Id,
        },
      ],
    };

    navigate(`/flights/reviewbooking`, {
      state: statePayload,
    });

    // const baseUrl = window.location.origin;
    // const newTabUrl = `${baseUrl}/flights/reviewbooking?paxType=A-${paxes?.ADULT}_C-${paxes?.CHILD}_I-${paxes?.INFANT}&Search_Key=${searchKey}&AirRepriceRequests=${priceDetails?.Fare_Id}_${flightKey}`;

    // window.open(newTabUrl, "_blank");
  };

  const startTime = useMemo(
    () => moment(route?.Segments[0]?.Departure_DateTime),
    [route]
  );
  const endTime = useMemo(
    () =>
      moment(route?.Segments[route?.Segments?.length - 1]?.Arrival_DateTime),
    [route]
  );

  const remainingSeats = priceDetails?.Seats_Available;
  // const cabinClass = priceDetails?.FareDetails[0]?.FareClasses.map(
  //   (fClass: any, index: number) =>
  //     getNewCabinClassnameFromCode(fClass?.Class_Code) +
  //     `${
  //       index + 1 < priceDetails?.FareDetails[0]?.FareClasses?.length
  //         ? ", "
  //         : ""
  //     }`
  // );

  const totalPrice = useMemo(() => {
    if (!priceDetails || !priceDetails.FareDetails) return 0;

    let totalAdultPrice = 0;
    let totalChildPrice = 0;
    let totalInfantPrice = 0;

    let adultBaseRate = 0;
    let childBaseRate = 0;
    let infantBaseRate = 0;

    let adultTaxRate = 0;
    let childTaxRate = 0;
    let infantTaxRate = 0;

    let adultFreeBaggage = {};
    let childFreeBaggage = {};
    let infantFreeBaggage = {};

    priceDetails.FareDetails.forEach((fare: any) => {
      // const totalAmount = fare.Total_Amount || 0;
      const totalAmount =
        (fare.Total_Amount || 0) - (fare.Service_Fee_Amount || 0);
      const BaseAmount = fare.Basic_Amount || 0;
      const TaxAmount = fare.AirportTax_Amount || 0;
      const freeBaggage = fare.Free_Baggage || {};

      switch (fare.PAX_Type) {
        case 0:
          totalAdultPrice += totalAmount * ADULT || 0;
          adultBaseRate += BaseAmount || 0;
          adultTaxRate += TaxAmount || 0;
          adultFreeBaggage = freeBaggage || 0;
          break;
        case 1:
          totalChildPrice += totalAmount * CHILD || 0;
          childBaseRate += BaseAmount || 0;
          childTaxRate += TaxAmount || 0;
          childFreeBaggage = freeBaggage;
          break;
        case 2:
          totalInfantPrice += totalAmount * INFANT || 0;
          infantBaseRate += BaseAmount || 0;
          infantTaxRate += TaxAmount || 0;
          infantFreeBaggage = freeBaggage;
          break;
        default:
          break;
      }
    });

    setAdultBaseRate(adultBaseRate);
    setChildBaseRate(childBaseRate);
    setInfantBaseRate(infantBaseRate);

    setAdultTaxRate(adultTaxRate);
    setChildTaxRate(childTaxRate);
    setInfantTaxRate(infantTaxRate);

    setAdultFreeBaggage(adultFreeBaggage);
    setChildFreeBaggage(childFreeBaggage);
    setInfantFreeBaggage(infantFreeBaggage);

    return totalAdultPrice + totalChildPrice + totalInfantPrice;
  }, [priceDetails, ADULT, CHILD, INFANT]);

  // const totalPrice = priceDetails?.FareDetails[0]?.Total_Amount;

  return (
    <div className="flight-outer">
      <div
        className={`flight-route ${
          selectedFlightPriceId === priceDetails?.Fare_Id
            ? "selected-flight-border"
            : ""
        }`}
      >
        <FlightRoute
          route={route}
          startTime={startTime}
          endTime={endTime}
          remainingSeats={remainingSeats}
          openFightDetail={openFightDetail}
          setOpenFlightDetail={setOpenFlightDetail}
        />
        <FlightPrice
          route={route}
          type={prefix}
          index={index}
          openFlightRate={openFlightRate}
          routeIndex={routeIndex}
          setOpenFlightRate={setOpenFlightRate}
          priceDetails={priceDetails}
          fareRule={fareRule}
          getFareRules={getFareRules}
          fareRulesLoading={fareRulesLoading}
          sortedPriceList={sortedPriceList}
          handleRadioButtonChange={handleRadioButtonChange}
          handleBookNowClick={handleBookNowClick}
          handleSelectFlight={handleSelectFlight}
          isSelected={selectedFlightPriceId === priceDetails?.Fare_Id}
        />
      </div>
      {/* flight-outer-open */}
      <FlightDetailsTabs
        openFightDetail={openFightDetail}
        routeIndex={routeIndex}
        route={route}
        priceDetails={priceDetails}
        // classOfBook={"classOfBook"}
        // cabinClass={cabinClass}
        remainingSeats={remainingSeats}
        getFareRules={getFareRules}
        ADULT={ADULT}
        adultTaxRate={adultTaxRate}
        adultBaseRate={adultBaseRate}
        CHILD={CHILD}
        childTaxRate={childTaxRate}
        childBaseRate={childBaseRate}
        INFANT={INFANT}
        infantTaxRate={infantTaxRate}
        infantBaseRate={infantBaseRate}
        totalPrice={totalPrice}
        fareRule={fareRule}
        fareRulesLoading={fareRulesLoading}
        adultFreeBaggage={adultFreeBaggage}
        childFreeBaggage={childFreeBaggage}
        infantFreeBaggage={infantFreeBaggage}
      />
    </div>
  );
};

export default FlightMainCard;
